/**
 * HotLeadsStories — S5 horizontal stories-style row of hot lead avatars.
 *
 * Layout:
 *   A single horizontally-scrollable row of circular initials avatars,
 *   positioned at the TOP of the mobile lead-card area (ContactDetail).
 *   Fade overlays on left/right edges indicate scrollability.
 *
 * Props:
 *   leads         Lead[]       — array of hot lead objects (id, first_name, last_name, hot_score)
 *   currentLeadId string|null  — id of the currently-viewed lead (gets a glow ring)
 *   onLeadSelect  (id) => void — called when an avatar is tapped
 *   isLoading     boolean      — show skeleton shimmer while data is loading
 *
 * Design system:
 *   Dark mode, consistent with MobileTopNav/MobileBottomToolbar palette (#0b1220 bg).
 *   Avatar bg: brand-blue gradient (var(--brand-primary, #317eeb) → #1d5fc4).
 *   Active ring: white + coral glow (matches hot-lead accent #fb7185).
 *   Skeleton: animated shimmer circles.
 *
 * Empty state: compact "No hot leads" text — no extra DOM noise.
 * Loading state: 5 shimmer skeleton circles.
 */

import React, { useRef, useState, useEffect, useLayoutEffect } from 'react';

// Inject keyframes + scrollbar-hide CSS once into the document
const STYLE_ID = 'hot-leads-stories-styles';
if (typeof document !== 'undefined' && !document.getElementById(STYLE_ID)) {
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes shimmer {
      0%   { background-position: -200% 0; }
      100% { background-position:  200% 0; }
    }
    .hot-leads-stories-scroll::-webkit-scrollbar {
      display: none;
    }
  `;
  document.head.appendChild(style);
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getInitials(firstName, lastName) {
  const f = (firstName || '').trim();
  const l = (lastName || '').trim();
  if (!f && !l) return '?';
  if (!l) return f[0].toUpperCase();
  if (!f) return l[0].toUpperCase();
  return (f[0] + l[0]).toUpperCase();
}

// A simple colour map so each avatar gets a consistent hue based on initials.
// This avoids every avatar looking identical and makes quick visual scanning easier.
const AVATAR_COLORS = [
  { bg: 'linear-gradient(135deg, var(--brand-primary, #317eeb) 0%, #1d5fc4 100%)', border: 'var(--brand-primary, #317eeb)' },
  { bg: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)', border: '#8b5cf6' },
  { bg: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', border: '#10b981' },
  { bg: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', border: '#f59e0b' },
  { bg: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)', border: '#ef4444' },
  { bg: 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)', border: '#ec4899' },
  { bg: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', border: '#06b6d4' },
  { bg: 'linear-gradient(135deg, #84cc16 0%, #65a30d 100%)', border: '#84cc16' },
];

function avatarColorFor(initials) {
  const code = (initials.charCodeAt(0) || 0) + (initials.charCodeAt(1) || 0);
  return AVATAR_COLORS[code % AVATAR_COLORS.length];
}

// ─── Skeleton Item ────────────────────────────────────────────────────────────

function SkeletonAvatar() {
  return (
    <div style={styles.avatarWrapper}>
      <div style={{ ...styles.avatarCircle, ...styles.skeletonCircle }} />
      <div style={{ ...styles.avatarLabel, ...styles.skeletonLabel }} />
    </div>
  );
}

// ─── Avatar Item ─────────────────────────────────────────────────────────────

function LeadAvatar({ lead, isActive, onSelect }) {
  const [pressed, setPressed] = useState(false);
  const initials = getInitials(lead.first_name, lead.last_name);
  const color = avatarColorFor(initials);
  const firstName = (lead.first_name || '').trim();
  const label = firstName || initials;

  return (
    <div
      style={styles.avatarWrapper}
      onClick={() => onSelect(lead.id)}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      onTouchStart={() => setPressed(true)}
      onTouchEnd={() => setPressed(false)}
      role="button"
      tabIndex={0}
      aria-label={`View lead ${(lead.first_name || '')} ${(lead.last_name || '')}`.trim()}
      aria-pressed={isActive}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(lead.id); } }}
    >
      {/* Outer glow ring for active state */}
      <div
        style={{
          ...styles.avatarRing,
          ...(isActive ? styles.avatarRingActive : {}),
        }}
      >
        <div
          style={{
            ...styles.avatarCircle,
            background: color.bg,
            opacity: pressed ? 0.7 : 1,
            transform: pressed ? 'scale(0.93)' : 'scale(1)',
          }}
        >
          <span style={styles.avatarInitials}>{initials}</span>
        </div>
      </div>

      {/* Name label below avatar */}
      <div
        style={{
          ...styles.avatarLabel,
          color: isActive ? '#f1f5f9' : '#94a3b8',
          fontWeight: isActive ? 600 : 400,
        }}
      >
        {label.length > 7 ? label.slice(0, 7) + '…' : label}
      </div>

      {/* Hot score micro-badge */}
      {typeof lead.hot_score === 'number' && (
        <div style={styles.scoreBadge}>
          {lead.hot_score}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function HotLeadsStories({
  leads = [],
  currentLeadId = null,
  onLeadSelect,
  isLoading = false,
}) {
  const scrollRef = useRef(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);

  // Update fade visibility on scroll
  const updateFades = () => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeftFade(el.scrollLeft > 8);
    setShowRightFade(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateFades();
    el.addEventListener('scroll', updateFades, { passive: true });
    // Also fire on resize
    const ro = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(updateFades)
      : null;
    if (ro) ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateFades);
      if (ro) ro.disconnect();
    };
  }, [leads, isLoading]);

  // ── Loading skeleton ───────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div style={styles.container} aria-label="Loading hot leads">
        <div style={styles.scrollRow}>
          {Array.from({ length: 5 }).map((_, i) => (
            <SkeletonAvatar key={i} />
          ))}
        </div>
      </div>
    );
  }

  // ── Empty state ────────────────────────────────────────────────────────────
  if (!leads || leads.length === 0) {
    return (
      <div style={{ ...styles.container, ...styles.emptyContainer }}>
        <span style={styles.emptyText}>No hot leads</span>
      </div>
    );
  }

  // ── Stories row ────────────────────────────────────────────────────────────
  return (
    <div style={styles.container}>
      {/* Left fade */}
      {showLeftFade && <div style={{ ...styles.fadeMask, ...styles.fadeMaskLeft }} />}

      <div
        ref={scrollRef}
        style={styles.scrollRow}
        className="hot-leads-stories-scroll"
        aria-label="Hot leads quick-switch row"
      >
        {leads.map((lead) => (
          <LeadAvatar
            key={lead.id}
            lead={lead}
            isActive={lead.id === currentLeadId}
            onSelect={onLeadSelect}
          />
        ))}
      </div>

      {/* Right fade */}
      {showRightFade && <div style={{ ...styles.fadeMask, ...styles.fadeMaskRight }} />}
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const AVATAR_SIZE = 52;
const RING_PAD = 3; // px between ring and circle

const styles = {
  container: {
    position: 'relative',
    width: '100%',
    background: '#0b1220',
    paddingTop: '12px',
    paddingBottom: '10px',
    borderBottom: '1px solid rgba(49,126,235,0.15)',
  },

  scrollRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: '14px',
    overflowX: 'auto',
    overflowY: 'hidden',
    paddingLeft: '14px',
    paddingRight: '14px',
    scrollbarWidth: 'none',        // Firefox
    msOverflowStyle: 'none',       // IE/Edge
    WebkitOverflowScrolling: 'touch',
    // Hide webkit scrollbar
    '&::-webkit-scrollbar': { display: 'none' },
  },

  avatarWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '5px',
    cursor: 'pointer',
    flexShrink: 0,
    position: 'relative',
    userSelect: 'none',
    WebkitTapHighlightColor: 'transparent',
  },

  avatarRing: {
    borderRadius: '50%',
    padding: `${RING_PAD}px`,
    border: '2px solid transparent',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  },

  avatarRingActive: {
    borderColor: '#fb7185',
    boxShadow: '0 0 0 2px rgba(251,113,133,0.35), 0 0 12px rgba(251,113,133,0.25)',
  },

  avatarCircle: {
    width: AVATAR_SIZE,
    height: AVATAR_SIZE,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'opacity 0.1s ease, transform 0.1s ease',
    boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
  },

  avatarInitials: {
    color: '#fff',
    fontSize: '16px',
    fontWeight: 700,
    fontFamily: "'Noto Sans', 'Lato', sans-serif",
    letterSpacing: '0.5px',
    pointerEvents: 'none',
  },

  avatarLabel: {
    fontSize: '10px',
    fontFamily: "'Noto Sans', 'Lato', sans-serif",
    textAlign: 'center',
    maxWidth: `${AVATAR_SIZE + RING_PAD * 2 + 4}px`,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    transition: 'color 0.15s ease',
  },

  scoreBadge: {
    position: 'absolute',
    top: '-2px',
    right: '-2px',
    background: '#fb7185',
    color: '#fff',
    fontSize: '9px',
    fontWeight: 700,
    fontFamily: "'Noto Sans', 'Lato', sans-serif",
    borderRadius: '8px',
    padding: '1px 4px',
    lineHeight: 1.3,
    minWidth: '16px',
    textAlign: 'center',
    border: '1.5px solid #0b1220',
    pointerEvents: 'none',
  },

  // ── Fade masks ──────────────────────────────────────────────────────────────
  fadeMask: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: '28px',
    pointerEvents: 'none',
    zIndex: 2,
  },
  fadeMaskLeft: {
    left: 0,
    background: 'linear-gradient(to right, #0b1220 0%, transparent 100%)',
  },
  fadeMaskRight: {
    right: 0,
    background: 'linear-gradient(to left, #0b1220 0%, transparent 100%)',
  },

  // ── Skeleton ────────────────────────────────────────────────────────────────
  skeletonCircle: {
    background: 'linear-gradient(90deg, #1e293b 25%, #2c3a50 50%, #1e293b 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.4s infinite',
  },
  skeletonLabel: {
    width: '36px',
    height: '8px',
    borderRadius: '4px',
    background: 'linear-gradient(90deg, #1e293b 25%, #2c3a50 50%, #1e293b 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.4s infinite',
  },

  // ── Empty state ─────────────────────────────────────────────────────────────
  emptyContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '72px',
  },
  emptyText: {
    color: '#475569',
    fontSize: '13px',
    fontFamily: "'Noto Sans', 'Lato', sans-serif",
    fontStyle: 'italic',
  },
};
